import { useCallback, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { differenceInCalendarDays } from 'date-fns';
import { DefaultValues, FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { ZodSchema } from 'zod';

import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import {
  TableFilterConfig,
  useTableFilters,
  UseTableFiltersReturn,
} from '@/components/data-table/filters-v2';
import { FilterValues } from '@/hooks/use-filter-values';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { formatDate } from '@/lib/format';
import {
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { CUSTOM_ATTR_FILTER_PREFIX } from '@/modules/common/utils/custom-attribute-filters';
import { dismissFloatingLayers } from '@/modules/finance/common/utils/use-guarded-filter-state';

interface EntityPageFilterKeys {
  billingPeriod: string;
  search: string;
  /** Keys whose value is an array of string ids (e.g. client, project, member) */
  arrayKeys: readonly string[];
}

interface UnsavedChangesGuardProps {
  isModalOpen: boolean;
  onDiscard: () => void;
  onCancel: () => void;
  entity: string;
}

interface UseGenerateEntityPageConfig<TFormValues extends FieldValues> {
  filterKeys: EntityPageFilterKeys;
  filtersStorageKey: string;
  filterKeysList: readonly string[];
  filtersConfig: TableFilterConfig[];
  formSchema: ZodSchema<TFormValues>;
  formDefaultValues?: DefaultValues<TFormValues>;
  unsavedChangesEntityLabel: string;
  /**
   * Page-level dirty signal OR-ed with the form's own dirty fields. Use this
   * when the page tracks additional state outside the form (e.g. user-added
   * overhead or extra member rows) that should also trip the unsaved-changes
   * guard.
   */
  additionalIsDirty?: boolean;
  /** Reset hook for page-level state cleared alongside form.reset() on discard. */
  onDiscardAdditional?: () => void;
}

interface UseGenerateEntityPageReturn<TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  unsavedChangesGuard: ReturnType<typeof useUnsavedChangesGuard>;
  unsavedChangesGuardProps: UnsavedChangesGuardProps;
  filterState: UseTableFiltersReturn;

  dates: { startDate: Date; endDate: Date } | null;
  selectedPreset: DateRangePreset | null;
  handleDateChange: (newDates: DateRangeV2) => void;
  handlePresetSelect: (preset: DateRangePreset) => void;
  handleDateReset: () => void;

  appliedFilters: FilterValues['values'];
  hasSearch: boolean;
  isAnyFilterApplied: boolean;
  isBillingPeriodSelected: boolean;

  /** Raw URL value (the serialized date range string) — pass straight to modals */
  billingPeriodValue: string;
  /** Pretty-formatted "MMM d, yyyy - MMM d, yyyy" label, or '' when unset */
  billingPeriodLabel: string;

  /** Call after a successful generate to bypass the unsaved-changes guard */
  handleGenerateSuccess: () => void;
}

const formatBillingPeriodDate = (date: Date): string =>
  formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });

// Largest billing range the generate screens can safely load eagerly. The picker
// now caps presets at Month (Year/Quarter are hidden), so a range wider than this
// can only arrive via a stale URL carried from the bills/invoices LIST page — which
// would crash the backend. We treat such a range as unselected so the billing-period
// gate re-prompts for a safe range.
// TODO(lazyload): drop this clamp once generate is paginated and large ranges are safe.
const MAX_BILLING_PERIOD_DAYS = 31;

/**
 * Bundles every page-level concern shared between the generate-invoices and
 * generate-bills pages: filter persistence, the form + unsaved-changes guard,
 * a guarded v2 filter state, billing-period URL state, the legacy applied-
 * filters shape consumed by the data hooks, and derived flags.
 *
 * Entity-specific concerns (data hook, smart refresh, column definitions,
 * modals, zero-totals dirty check, the checkbox-pulse hint) stay in the page
 * itself or in `useGenerateSelectionState`.
 */
export const useGenerateEntityPage = <TFormValues extends FieldValues>({
  filterKeys,
  filtersStorageKey,
  filterKeysList,
  filtersConfig,
  formSchema,
  formDefaultValues,
  unsavedChangesEntityLabel,
  additionalIsDirty = false,
  onDiscardAdditional,
}: UseGenerateEntityPageConfig<TFormValues>): UseGenerateEntityPageReturn<TFormValues> => {
  usePersistedTableFilters({
    storageKey: filtersStorageKey,
    filterKeys: [...filterKeysList],
  });

  const rawFilterState = useTableFilters(filtersConfig);

  const form = useForm<TFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  // RHF's isDirty is unreliable here: form.reset() clears defaultValues to {},
  // and field registration after new data loads causes isDirty to flip back to
  // true even without user edits. dirtyFields is more precise — it only contains
  // fields explicitly set with shouldDirty: true (i.e. real user edits).
  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;

  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: hasDirtyFields || additionalIsDirty,
    onDiscard: () => {
      form.reset();
      onDiscardAdditional?.();
    },
  });

  // v2 filter mutations (member/project/client/search/billing-model) preserve edits: the
  // form is now keyed by entity id, so a filter that re-shows an entity still carries its
  // edit. Only the date-range change is gated (see handleDateChange) — it reloads a
  // different dataset, so its edits are discarded via the unsaved-changes modal.
  const filterState = rawFilterState;

  const [searchParams, setSearchParams] = useSearchParams();

  const { dates, selectedPreset } = useMemo(() => {
    const raw = searchParams.get(filterKeys.billingPeriod);
    const parsed = raw ? parseDateRange(raw) : null;
    if (!parsed) return { dates: null, selectedPreset: null };
    // Reject an oversized range carried in from the list page — it would crash the
    // backend on this eager-load screen. Treat as unselected so the picker gate shows.
    const spanDays = differenceInCalendarDays(parsed.endDate, parsed.startDate);
    if (spanDays > MAX_BILLING_PERIOD_DAYS) {
      return { dates: null, selectedPreset: null };
    }
    return { dates: parsed, selectedPreset: detectPresetFromDates(parsed) };
  }, [searchParams, filterKeys.billingPeriod]);

  const applyDateChange = useCallback(
    (newDates: DateRangeV2) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev.toString());
          next.set(filterKeys.billingPeriod, serializeDateRange(newDates));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, filterKeys.billingPeriod]
  );

  const applyDateReset = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev.toString());
        next.delete(filterKeys.billingPeriod);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, filterKeys.billingPeriod]);

  const handleDateChange = useCallback(
    (newDates: DateRangeV2) => {
      if (unsavedChangesGuard.checkUnsavedChanges(() => applyDateChange(newDates))) {
        applyDateChange(newDates);
      } else {
        dismissFloatingLayers();
      }
    },
    [applyDateChange, unsavedChangesGuard]
  );

  const handlePresetSelect = useCallback(
    (preset: DateRangePreset) => {
      handleDateChange(getDateRangeFromPreset(preset));
    },
    [handleDateChange]
  );

  const handleDateReset = useCallback(() => {
    if (unsavedChangesGuard.checkUnsavedChanges(applyDateReset)) {
      applyDateReset();
    } else {
      dismissFloatingLayers();
    }
  }, [applyDateReset, unsavedChangesGuard]);

  // Depend on activeFilters (URL-derived, stable between renders) rather than the
  // full filterState wrapper object, which is recreated on every render by
  // useGuardedFilterState and would otherwise cause the query key to change constantly.
  const appliedFilters = useMemo<FilterValues['values']>(() => {
    // Treat a rejected (oversized) range as no billing period — `dates` is the
    // validated source of truth — so the gate, the data query, and the modal all
    // agree it's unselected rather than firing a query for the unsafe raw range.
    const billingPeriod = dates ? (searchParams.get(filterKeys.billingPeriod) ?? '') : '';
    const result: FilterValues['values'] = {
      [filterKeys.billingPeriod]: billingPeriod,
      [filterKeys.search]: filterState.getFilterValue(filterKeys.search)[0] ?? '',
    };
    filterKeys.arrayKeys.forEach(key => {
      result[key] = filterState.getFilterValue(key);
    });
    Object.keys(filterState.activeFilters).forEach(key => {
      if (key.startsWith(CUSTOM_ATTR_FILTER_PREFIX)) {
        result[key] = filterState.getFilterValue(key);
      }
    });
    return result;
  }, [searchParams, filterState.activeFilters, filterKeys, dates]);

  // NOTE: no form.reset() on filter change. Fields are keyed by entity id, so edits survive
  // filter changes (a filter that re-shows an entity resolves the same path). Only a
  // date-range change discards edits — that path resets the form via the guard's onDiscard.

  const hasSearch = Boolean((appliedFilters[filterKeys.search] as string)?.length);
  const isBillingPeriodSelected = Boolean(appliedFilters[filterKeys.billingPeriod]);
  const isAnyFilterApplied =
    isBillingPeriodSelected ||
    hasSearch ||
    filterKeys.arrayKeys.some(key => (appliedFilters[key] as string[])?.length > 0);

  const unsavedChangesGuardProps = useMemo<UnsavedChangesGuardProps>(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
      entity: unsavedChangesEntityLabel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
      unsavedChangesEntityLabel,
    ]
  );

  const billingPeriodValue = (appliedFilters[filterKeys.billingPeriod] as string | undefined) ?? '';
  const billingPeriodLabel = useMemo(() => {
    if (!billingPeriodValue) return '';
    const range = parseDateRange(billingPeriodValue);
    if (!range?.startDate || !range?.endDate) return '';
    return `${formatBillingPeriodDate(range.startDate)} - ${formatBillingPeriodDate(range.endDate)}`;
  }, [billingPeriodValue]);

  const handleGenerateSuccess = useCallback(() => {
    unsavedChangesGuard.allowNavigation();
  }, [unsavedChangesGuard]);

  return {
    form,
    unsavedChangesGuard,
    unsavedChangesGuardProps,
    filterState,
    dates,
    selectedPreset,
    handleDateChange,
    handlePresetSelect,
    handleDateReset,
    appliedFilters,
    hasSearch,
    isAnyFilterApplied,
    isBillingPeriodSelected,
    billingPeriodValue,
    billingPeriodLabel,
    handleGenerateSuccess,
  };
};
